import React from 'react'
import { DocumentNode } from 'graphql'
import { PureQueryOptions } from 'apollo-client'
import { ApolloConsumer } from 'react-apollo'
import { AggregationOptions } from '../aggregations'
import { QuickFilter } from '../../legacy-design-system/quick-filter'
import { Query } from '../graphql'
import { CollectionVariables } from '../organization-collection-loader'

interface ChildProps {
  readonly quickFilters: QuickFilter[]
  readonly loading: boolean
  readonly queryInfo?: PureQueryOptions
}

interface Props<TAggregationCategory extends string> {
  readonly query?: DocumentNode
  readonly queryRootKey?: string
  readonly organizationID: string
  readonly applicationID?: string
  readonly filterText?: string
  readonly aggregationFilters?: AggregationOptions<TAggregationCategory>
  readonly licensedOnly?: boolean
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly areVariablesEqual?: (
    var1: CollectionVariables,
    var2: CollectionVariables
  ) => boolean
  readonly children: (props: ChildProps) => React.ReactNode
}

interface VarsWithoutAgg {
  readonly organizationID: string
  readonly filterText?: string
}

type Vars<TAggregationCategory extends string> = VarsWithoutAgg &
  AggregationOptions<TAggregationCategory>

export function QuickFiltersLoader<TAggregationCategory extends string>({
  query,
  queryRootKey = '',
  organizationID,
  applicationID,
  filterText,
  aggregationFilters,
  additionalFilters,
  areVariablesEqual,
  licensedOnly,
  children
}: Props<TAggregationCategory>) {
  const variables: Vars<TAggregationCategory> = Object.assign(
    {},
    aggregationFilters,
    {
      organizationID,
      applicationID,
      filterText,
      licensedOnly,
      ...additionalFilters
    }
  )

  if (!query) {
    return children({
      quickFilters: [],
      loading: false
    }) as React.ReactElement
  }

  return (
    <ApolloConsumer>
      {(client) => (
        <Query<any>
          client={client}
          query={query}
          variables={variables}
          dataKey={queryRootKey}
          areVariablesEqual={areVariablesEqual}
        >
          {({ loading, data: quickFilters = [] }) =>
            children({
              quickFilters,
              loading,
              queryInfo: {
                query,
                variables
              }
            })
          }
        </Query>
      )}
    </ApolloConsumer>
  )
}
